// SideBar.jsx
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CreditIcon from "../ui/icons/CreditIcon";
import {
  AI_HOME_PATHNAME,
  DASHBOARD_PATHNAME,
  JOB_PLANNING_PATHNAME,
  JOB_STATUS_PATHNAME,
  NETWORK_PATHNAME,
  SEARCHED_PATHNAME,
  STATISTICS_PATHNAME,
} from "@/constants/pathname";

import useUserStore from "@/stores/user";
import HomeIcon from "../ui/icons/HomeIcon";
import JobIcon from "../ui/icons/JobIcon";
import SearchedIcon from "../ui/icons/SearchedIcon";
import StatisticsIcon from "../ui/icons/StatisticsIcon";
import BioinformaticsIcon from "../ui/icons/BioinformaticsIcon";
import AiIcon from "../ui/icons/AiIcon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import useSideBarStore from "@/stores/side-bar";
import { Text } from "../ui/text";
import ActiveHomeIcon from "../ui/icons/ActiveHomeIcon";
import ActiveJobIcon from "../ui/icons/ActiveJobIcon";
import ActiveSearchedIcon from "../ui/icons/ActiveSearchedIcon";
import ActiveStatisticsIcon from "../ui/icons/ActiveStatisticsIcon";
import ActiveBioinformaticsIcon from "../ui/icons/ActiveBioinformaticsIcon";
import useUserQuery from "@/hooks/services/queries/user/use-user-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "react-i18next";
import useUserMeteringQuery from "@/hooks/services/queries/user/use-user-metering-query";
import CreditBox from "../credit/credit-box";
import useAccountInfoQuery from "@/hooks/services/queries/use-account-info-query";
import { MeteringData } from "@/types/user";
import ActiveAiIcon from "../ui/icons/ActiveAiIcon";

dayjs.extend(utc);
dayjs.extend(timezone);

const SideBar = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();

  const { data: userData } = useUserQuery();

  const { data: meteringData, isLoading: isMeteringLoading } =
    useUserMeteringQuery({ userId: userData?.id });

  const { data: accountInfo, isLoading: isAccountInfoLoading } =
    useAccountInfoQuery();

  const { isOpen, toggleSideBar } = useSideBarStore();

  const NAVS = [
    {
      name: t("layout.home"),
      href: DASHBOARD_PATHNAME,
      icon: HomeIcon,
      activeIcon: ActiveHomeIcon,
      alias: "home",
    },
    {
      name: t("layout.job"),
      href: JOB_STATUS_PATHNAME,
      icon: JobIcon,
      activeIcon: ActiveJobIcon,
      alias: "job",
      children: [
        {
          name: t("layout.status"),
          href: JOB_STATUS_PATHNAME,
          alias: "job-status",
        },
        {
          name: t("layout.planning"),
          href: JOB_PLANNING_PATHNAME,
          alias: "job-planning",
        },
      ],
    },
    {
      name: t("layout.searched"),
      href: SEARCHED_PATHNAME,
      icon: SearchedIcon,
      activeIcon: ActiveSearchedIcon,
      alias: "searched",
    },
    {
      name: t("layout.statistics"),
      href: STATISTICS_PATHNAME,
      icon: StatisticsIcon,
      activeIcon: ActiveStatisticsIcon,
      alias: "statistics",
    },
    {
      name: t("layout.bioinformatics"),
      href: NETWORK_PATHNAME,
      icon: BioinformaticsIcon,
      activeIcon: ActiveBioinformaticsIcon,
      alias: "bioinformatics",
    },
    {
      name: t("layout.ai"),
      href: AI_HOME_PATHNAME,
      icon: AiIcon,
      activeIcon: ActiveAiIcon,
      alias: "ai",
    },
  ];

  const [isPopover, setIsPopover] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const clickMenu = (name: string, href: string) => {
    if (name !== t("layout.job")) {
      navigate(href);
    } else if (!isOpen) {
      setIsPopover(!isPopover);
    }
  };

  useEffect(() => {
    setIsPopover(false);
  }, [location.pathname]);

  return (
    <aside className=" bg-gray-50 border-r border-gray-200">
      {/* 축소된 메뉴 */}
      {!isOpen && (
        <nav className="flex flex-col h-full px-[16px] pt-[32px] pb-[32px] justify-between">
          <ul className="flex flex-col gap-1">
            {NAVS.map((nav, idx) => (
              <li key={idx}>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger>
                      <div
                        data-testid={`sidebar-icon-${nav.name}`}
                        className="w-10 h-10 flex justify-center items-center cursor-pointer"
                        onClick={() => clickMenu(nav.name, nav.href)}
                      >
                        {location.pathname.includes(nav.alias) ? (
                          <nav.activeIcon />
                        ) : (
                          <nav.icon />
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">{nav.name}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {isPopover && nav.name === t("layout.job") && (
                  <div
                    data-testid="job-popover"
                    className="click-menu absolute left-20 top-40 bg-primary-40 rounded-xl shadow-lg z-20"
                  >
                    {nav.children &&
                      nav.children.map((child, index) => (
                        <div key={index} className="click-menu-item p-3">
                          {(child.alias !== "job-planning" ||
                            user.isSearchable) && (
                            <Link
                              to={child.href}
                              className="text-white font-semibold"
                            >
                              {child.name}
                            </Link>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {accountInfo?.isSaaS && userData?.role === 1 && (
            <div className="flex justify-center">
              <CreditIcon />
            </div>
          )}
        </nav>
      )}

      {/* 펼쳐진 메뉴 */}
      {isOpen && (
        <nav className="flex flex-col h-full pl-[16px] pr-[14px] pt-[32px] pb-[32px] gap-5 w-[187px] justify-between">
          <ul className="flex flex-col gap-[4px]">
            {NAVS.map((nav, idx) => (
              <li key={idx} className="">
                <Link
                  to={nav.href}
                  className={`flex items-center gap-1 ${
                    location.pathname.includes(nav.alias)
                      ? "active font-bold"
                      : ""
                  }`}
                  onClick={() => {
                    if (nav.alias === "ai") {
                      toggleSideBar();
                    }
                  }}
                >
                  <div className="w-10 h-10 flex justify-center items-center">
                    {location.pathname.includes(nav.alias) ? (
                      <nav.activeIcon />
                    ) : (
                      <nav.icon />
                    )}
                  </div>
                  <Text
                    variant={"title-md"}
                    style={{
                      color: location.pathname.includes(nav.alias)
                        ? "#3FD8C9"
                        : "#999999",
                    }}
                    className="w-[110px]"
                  >
                    {nav.name}
                  </Text>
                </Link>
                {location.pathname.includes(nav.alias) && nav.children && (
                  <div className="pb-[16px]">
                    {nav.children.map((child, index) => (
                      <div
                        key={index}
                        className="flex items-center pl-12 py-[6px]"
                      >
                        {(child.alias !== "job" || user.isSearchable) && (
                          <Link
                            to={child.href}
                            style={{
                              color: isActive(child.href)
                                ? "#3FD8C9"
                                : "#999999",
                            }}
                            className="font-semibold"
                          >
                            {child.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {accountInfo?.isSaaS && (
            <>
              {isAccountInfoLoading || isMeteringLoading ? (
                <div className="h-[100px] w-[151px] bg-gray-200 animate-pulse">
                  Loading...
                </div>
              ) : (
                <CreditBox
                  userData={userData}
                  meteringData={meteringData as MeteringData}
                />
              )}
            </>
          )}
        </nav>
      )}
    </aside>
  );
};

export default SideBar;
